import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Card } from '../components/Card';
import { StatusBadge } from '../components/billing';
import { varinymAPI } from '../lib/varinym-api';

export function JobHistory() {
  const { data: jobsData, isLoading } = useQuery({
    queryKey: ['jobs', 'all'],
    queryFn: () => varinymAPI.listJobs(50, 0),
    refetchInterval: 5000, // Refresh every 5 seconds
  });

  return (
    <div>
      <h1 className="text-2xl font-bold text-foreground mb-6">Job History</h1>

      <Card>
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : jobsData?.jobs && jobsData.jobs.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Job ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Operation
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Progress
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Success Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Duration
                  </th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {jobsData.jobs.map((job) => {
                  const successRate = job.processed_items > 0
                    ? ((job.successful_items / job.processed_items) * 100).toFixed(1)
                    : '0';
                  const duration = job.completed_at
                    ? ((job.completed_at - job.created_at) / 60).toFixed(1)
                    : null;

                  return (
                    <tr key={job.job_id} className="hover:bg-accent/50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">
                        <Link
                          to={`/bulk/jobs/${job.job_id}`}
                          className="text-primary hover:underline"
                        >
                          {job.job_id.slice(0, 8)}...
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                        {job.operation.startsWith('csv_transform_')
                          ? `Transform (${job.operation.replace('csv_transform_', '')})`
                          : job.operation}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge type="job" value={job.status} />
                        {job.status === 'processing' && (
                          <span className="ml-1 animate-pulse text-info">...</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-foreground">
                          {job.processed_items} / {job.total_items}
                        </div>
                        <div className="w-full bg-muted rounded-full h-1.5 mt-1">
                          <div
                            className="bg-primary h-1.5 rounded-full transition-all duration-300"
                            style={{
                              width: `${(job.processed_items / job.total_items) * 100}%`,
                            }}
                          ></div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span
                          className={
                            parseFloat(successRate) >= 95
                              ? 'text-success font-medium'
                              : parseFloat(successRate) >= 80
                              ? 'text-warning'
                              : 'text-destructive'
                          }
                        >
                          {successRate}%
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {new Date(job.created_at * 1000).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {duration ? `${duration} min` : job.status === 'processing' ? '...' : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">No bulk jobs yet</p>
            <Link to="/bulk" className="link-bare">
              <button className="text-primary hover:text-primary/80 font-medium">
                Create First Job →
              </button>
            </Link>
          </div>
        )}
      </Card>
    </div>
  );
}
